import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { find, getParent, elementIs, isEnabled, addClass, removeClass } from '@fretadao/f-js-dom';
import { isBlank } from '@fretadao/f-js-utils';

/*
 * Manage field disclaimer indicators required and optiona.
 *
 * Usage:
 *
 * <form data-controller="requirement-indication">
 *   <p>
 *     <label>Name</labe>
 *     <input type="text" name="name" data-requirement-indication-target="field" />
 *   </p>
 *   <p>
 *     <label>Email</labe>
 *     <input type="text" name="email" required="required" data-requirement-indication-target="field" />
 *   </p>
 *   <button type="submit">Send</button>
 * </form>
 *
 */
application.register('requirement-indication', class extends Controller {
  static targets = ['field'];
  static classes = ['required', 'optional'];

  connect() {
    this.#indicateRequirements();
    this.#observeFields();
  }

  #observeFields() {
    const observerConfig = { attributes: true, childList: false, characterData: true };
    const observer = new MutationObserver(mutations => mutations.forEach(mutation => this.#changeIndication(mutation)));

    this.fieldTargets.forEach(field => observer.observe(field, observerConfig));
  }

  #indicateRequirements() {
    this.fieldTargets.forEach(field => this.#indicateRequirement(field));
  }

  #indicateRequirement(field) {
    const fieldLabel = this.#fieldLabel(this.#fieldContainer(field));

    this.#createIndication(field, fieldLabel);
  }

  #changeIndication(mutation) {
    if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
      this.#indicateRequirement(mutation.target);
    }
  }

  #fieldContainer(field) {
    return getParent(field);
  }

  #fieldLabel(fieldContainer) {
    return find('label', fieldContainer);
  }

  #indicationClass(field) {
    return (elementIs('required', field)) ? this.requiredClass : this.optionalClass;
  }

  #clearIndication(fieldLabel) {
    removeClass(fieldLabel, [this.requiredClass, this.optionalClass]);
  }

  #createIndication(field, fieldLabel) {
    this.#clearIndication(fieldLabel);
    const indicationClass = this.#indicationClass(field);

    if (isEnabled(field)) addClass(fieldLabel, indicationClass);
  }
});
